import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import UserManagement from './UserManagement';
import AppManagement from './AppManagement';
import NASServer from './NASServer';
import KnowledgeBase from './KnowledgeBase';
import ProjectManagement from './ProjectManagement';
import SettingsSection from './SettingsSection';

const Dashboard = () => {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('main');
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('visor_sidebar_open') !== 'false');
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [resources, setResources] = useState({
    cpu: { value: '0%', loading: true, health: 'good' },
    temp: { value: '0°C', loading: true, health: 'good' },
    ram: { value: '0%', loading: true, health: 'good' },
    disk: { value: '0%', loading: true, health: 'good' }
  });
  const [serverTime, setServerTime] = useState('');
  const [uptime, setUptime] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem('visor_sidebar_open', String(sidebarOpen));
  }, [sidebarOpen]);

  useEffect(() => {
    const section = searchParams.get('section');
    if (section === 'projects') setActiveSection('projects');
  }, [searchParams]);

  useEffect(() => { if (!error) return; const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }, [error]);

  const formatUptime = (seconds) => {
    if (!seconds) return '';
    const days = Math.floor(seconds / (3600 * 24));
    const hours = Math.floor((seconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}д ${hours}ч`;
    } else if (hours > 0) {
      return `${hours}ч ${minutes}м`;
    } else {
      return `${minutes}м`;
    }
  };

  const formatBytes = (bytes) => {
    if (bytes == null || bytes === 0) return '—';
    const gb = bytes / (1024 ** 3);
    return `${gb.toFixed(1)} ГБ`;
  };

  const fetchApps = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get('/api/apps/?active_only=true', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 10000
      });
      setApps(response.data.apps);
    } catch (error) {
      console.error('Ошибка загрузки приложений:', error);
    } finally {
      setLoadingApps(false);
    }
  };

  const fetchSystemStats = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get('/api/system-stats', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 10000
      });
      
      const stats = response.data;
      
      const getHealthStatus = (value, type = 'percent') => {
        if (type === 'temp') {
          if (value > 70) return 'critical';
          if (value > 60) return 'warning';
          return 'good';
        } else {
          if (value > 80) return 'critical';
          if (value > 60) return 'warning';
          return 'good';
        }
      };

      setResources(prev => ({
        cpu: { 
          value: `${Math.round(stats.cpu_percent)}%`, 
          loading: false, 
          health: getHealthStatus(stats.cpu_percent),
          raw: stats.cpu_percent
        },
        temp: { 
          value: stats.cpu_temperature ? `${stats.cpu_temperature}°C` : 'N/A', 
          loading: false, 
          health: stats.cpu_temperature ? getHealthStatus(stats.cpu_temperature, 'temp') : 'good',
          raw: stats.cpu_temperature
        },
        ram: { 
          value: `${Math.round(stats.memory_percent)}%`, 
          loading: false, 
          health: getHealthStatus(stats.memory_percent),
          raw: stats.memory_percent
        },
        disk: { 
          value: (stats.disk_used != null && stats.disk_total != null)
            ? `${formatBytes(stats.disk_used)} / ${formatBytes(stats.disk_total)}`
            : `${Math.round(stats.disk_percent)}%`,
          loading: false,
          health: getHealthStatus(stats.disk_percent),
          raw: stats.disk_percent
        }
      }));

      if (stats.uptime) {
        setUptime(formatUptime(stats.uptime));
      }

    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  const fetchServerTime = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get('/api/server-time', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 5000
      });
      
      if (response.data && response.data.formatted) {
        setServerTime(response.data.formatted);
      }
    } catch (error) {
      console.error('Ошибка загрузки времени сервера:', error);
    }
  };

  const launchApp = (url) => {
    if (url) {
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'http://' + url;
      }
      window.open(url, '_blank');
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.get('/api/users/me/', {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: 10000
        });
        setUser(response.data);
        
        await fetchApps();
        await fetchSystemStats();
        await fetchServerTime();
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        } else {
          setError('Не удалось загрузить данные пользователя. Пожалуйста, попробуйте снова.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();

    const statsInterval = setInterval(() => {
      fetchSystemStats();
    }, 5000);

    const timeInterval = setInterval(() => {
      fetchServerTime();
    }, 60000);

    const appsInterval = setInterval(() => {
      fetchApps();
    }, 30000);

    return () => {
      clearInterval(statsInterval);
      clearInterval(timeInterval);
      clearInterval(appsInterval);
    };
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'cpu': return '⚡';
      case 'temp': return '🌡️';
      case 'ram': return '💾';
      case 'disk': return '💿';
      default: return '📊';
    }
  };

  const getHealthText = (health) => {
    switch (health) {
      case 'good': return 'Норма';
      case 'warning': return 'Высокая';
      case 'critical': return 'Критическая';
      default: return '';
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'main':
        return (
          <div className="content-area">
            <div className="content-header">
              <div>
                <h1>Системный мониторинг</h1>
                <p>Мониторинг ресурсов сервера</p>
              </div>
            </div>
            
            <div className="system-monitoring">
              <h3>Статистика системы</h3>
              <div className="system-stats">
                <div className="stat-card">
                  <div className={`stat-value ${resources.cpu.raw > 80 ? 'temp-warning' : ''}`}>
                    {resources.cpu.value}
                  </div>
                  <div className="stat-label">Загрузка CPU</div>
                  <div className={`resource-health health-${resources.cpu.health}`}>
                    {getHealthText(resources.cpu.health)}
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className={`stat-value ${resources.temp.raw > 60 ? 'temp-warning' : 'temp-normal'}`}>
                    {resources.temp.value}
                  </div>
                  <div className="stat-label">Температура CPU</div>
                  <div className={`resource-health health-${resources.temp.health}`}>
                    {getHealthText(resources.temp.health)}
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className={`stat-value ${resources.ram.raw > 80 ? 'temp-warning' : ''}`}>
                    {resources.ram.value}
                  </div>
                  <div className="stat-label">Использование RAM</div>
                  <div className={`resource-health health-${resources.ram.health}`}>
                    {getHealthText(resources.ram.health)}
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className={`stat-value ${resources.disk.raw > 80 ? 'temp-warning' : ''}`}>
                    {resources.disk.value}
                  </div>
                  <div className="stat-label">Диск (раздел приложения): занято / всего</div>
                  <div className={`resource-health health-${resources.disk.health}`}>
                    {getHealthText(resources.disk.health)}
                  </div>
                </div>
              </div>
              
              {uptime && (
                <div className="info" style={{ marginTop: '20px' }}>
                  Время работы системы: {uptime}
                </div>
              )}
              
              {serverTime && (
                <div className="info" style={{ marginTop: '10px' }}>
                  Текущее время сервера (Москва): {serverTime}
                </div>
              )}
            </div>
          </div>
        );
      
      case 'users':
        if (!user?.is_admin) {
          return (
            <div className="content-area">
              <div className="empty-content">
                <p style={{ color: 'var(--text-secondary)' }}>Доступ к разделу «Пользователи» только у пользователей с правами администратора.</p>
              </div>
            </div>
          );
        }
        return (
          <div className="content-area">
            <UserManagement currentUser={user} />
          </div>
        );
      
      case 'apps':
        if (!user?.is_admin) {
          return (
            <div className="content-area">
              <div className="empty-content">
                <p style={{ color: 'var(--text-secondary)' }}>Доступ к разделу «Управление приложениями» только у пользователей с правами администратора.</p>
              </div>
            </div>
          );
        }
        return (
          <div className="content-area">
            <AppManagement currentUser={user} onAppUpdated={fetchApps} />
          </div>
        );
      
      case 'nas':
        return (
          <div className="content-area">
            <NASServer />
          </div>
        );
      
      case 'wiki':
        return (
          <div className="content-area">
            <KnowledgeBase />
          </div>
        );
      
      case 'projects':
        return (
          <div className="content-area">
            <ProjectManagement currentUser={user} initialProjectId={searchParams.get('projectId')} initialTab={searchParams.get('tab')} initialConferenceId={searchParams.get('conferenceId')} />
          </div>
        );
      
      case 'settings':
        if (!user?.is_admin) {
          return (
            <div className="content-area">
              <div className="empty-content">
                <p style={{ color: 'var(--text-secondary)' }}>Доступ к настройкам только у пользователей с правами администратора.</p>
              </div>
            </div>
          );
        }
        return (
          <div className="content-area">
            <SettingsSection currentUser={user} />
          </div>
        );
      
      default:
        return (
          <div className="content-area">
            <div className="empty-content">
              <div className="empty-icon">📊</div>
              <h2 className="empty-title">Главная страница</h2>
              <p className="empty-description">
                Выберите раздел в боковом меню для просмотра информации.
              </p>
            </div>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard">
        <div className="loading">Загрузка панели управления...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="top-bar">
        <div className="top-bar-left">
          <button
            type="button"
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? 'Скрыть боковую панель' : 'Показать боковую панель'}
            aria-label={sidebarOpen ? 'Скрыть боковую панель' : 'Показать боковую панель'}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <div className="system-title">Visor-ECO</div>
        </div>
        <div className="resources-container">
          <div className={`resource-item cpu ${resources.cpu.loading ? 'updating' : ''}`}>
            <div className="resource-icon">{getResourceIcon('cpu')}</div>
            <div className="resource-details">
              <div className="resource-value">{resources.cpu.value}</div>
              <div className="resource-label">CPU</div>
              {!resources.cpu.loading && (
                <div className={`resource-health health-${resources.cpu.health}`}>
                  {getHealthText(resources.cpu.health)}
                </div>
              )}
            </div>
          </div>

          <div className={`resource-item temp ${resources.temp.loading ? 'updating' : ''}`}>
            <div className="resource-icon">{getResourceIcon('temp')}</div>
            <div className="resource-details">
              <div className="resource-value">{resources.temp.value}</div>
              <div className="resource-label">Температура</div>
              {!resources.temp.loading && resources.temp.value !== 'N/A' && (
                <div className={`resource-health health-${resources.temp.health}`}>
                  {getHealthText(resources.temp.health)}
                </div>
              )}
            </div>
          </div>

          <div className={`resource-item ram ${resources.ram.loading ? 'updating' : ''}`}>
            <div className="resource-icon">{getResourceIcon('ram')}</div>
            <div className="resource-details">
              <div className="resource-value">{resources.ram.value}</div>
              <div className="resource-label">RAM</div>
              {!resources.ram.loading && (
                <div className={`resource-health health-${resources.ram.health}`}>
                  {getHealthText(resources.ram.health)}
                </div>
              )}
            </div>
          </div>

          <div className={`resource-item disk ${resources.disk.loading ? 'updating' : ''}`}>
            <div className="resource-icon">{getResourceIcon('disk')}</div>
            <div className="resource-details">
              <div className="resource-value">{resources.disk.value}</div>
              <div className="resource-label">Диск (раздел приложения)</div>
              {!resources.disk.loading && (
                <div className={`resource-health health-${resources.disk.health}`}>
                  {getHealthText(resources.disk.health)}
                </div>
              )}
            </div>
          </div>

          <button className="logout-btn" onClick={handleLogout}>
            Выйти
          </button>
        </div>
      </div>

      <div className="main-content">
        <div className={`sidebar ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
          <div className="sidebar-section">
            <div className="sidebar-title">Главная</div>
            <div 
              className={`sidebar-item ${activeSection === 'main' ? 'active' : ''}`}
              onClick={() => setActiveSection('main')}
            >
              <span className="sidebar-icon">📊</span>
              Мониторинг системы
            </div>
            <div 
              className={`sidebar-item ${activeSection === 'nas' ? 'active' : ''}`}
              onClick={() => setActiveSection('nas')}
            >
              <span className="sidebar-icon">💾</span>
              NAS-Сервер
            </div>
            <div 
              className="sidebar-item"
              onClick={() => window.open('/mail/', '_blank')}
              title="Веб-почта Roundcube (логин: email, пароль — как в приложении)"
            >
              <span className="sidebar-icon">📧</span>
              Почта
            </div>
            <div 
              className={`sidebar-item ${activeSection === 'wiki' ? 'active' : ''}`}
              onClick={() => setActiveSection('wiki')}
            >
              <span className="sidebar-icon">📚</span>
              База знаний
            </div>
            <div 
              className={`sidebar-item ${activeSection === 'projects' ? 'active' : ''}`}
              onClick={() => setActiveSection('projects')}
            >
              <span className="sidebar-icon">🎯</span>
              Управление проектами
            </div>
          </div>
          
          {apps.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-title">Приложения</div>
              {loadingApps ? (
                <div className="sidebar-item">
                  <span className="sidebar-icon">⏳</span>
                  Загрузка...
                </div>
              ) : (
                apps.map((app) => (
                  <div 
                    key={app.id}
                    className="sidebar-app-item"
                    onClick={() => launchApp(app.url)}
                    title={`Запустить: ${app.name}\n${app.url}`}
                  >
                    <span className="sidebar-app-icon">{app.icon}</span>
                    <span className="sidebar-app-name">{app.name}</span>
                  </div>
                ))
              )}
              {user?.is_admin && (
              <div 
                className={`sidebar-item ${activeSection === 'apps' ? 'active' : ''}`}
                onClick={() => setActiveSection('apps')}
                style={{ marginTop: '15px' }}
              >
                <span className="sidebar-icon">⚙️</span>
                Управление приложениями
              </div>
              )}
            </div>
          )}
          
          <div className="sidebar-section">
            <div className="sidebar-title">Управление</div>
            {user?.is_admin && (
            <>
            <div 
              className={`sidebar-item ${activeSection === 'users' ? 'active' : ''}`}
              onClick={() => setActiveSection('users')}
            >
              <span className="sidebar-icon">👥</span>
              Пользователи
            </div>
            <div 
              className={`sidebar-item ${activeSection === 'apps' ? 'active' : ''}`}
              onClick={() => setActiveSection('apps')}
            >
              <span className="sidebar-icon">📱</span>
              Управление приложениями
            </div>
            <div 
              className={`sidebar-item ${activeSection === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveSection('settings')}
            >
              <span className="sidebar-icon">⚙️</span>
              Настройки
            </div>
            </>
            )}
          </div>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default Dashboard;
